import React from "react";
import { motion } from "framer-motion";
import {
  Activity, Boxes, GitBranch, ShieldCheck, Layers, Workflow, FileCheck2, MonitorCheck,
  BarChart3, ClipboardCheck, ScrollText, Lightbulb, Users, BookOpen, Library, Gauge,
  Server, Cloud, Lock, History, ArrowUpRight,
} from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, Stagger, staggerItem } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { SectionHeading, Overline } from "@/components/ui/primitives";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/i18n/router";
import { useLang } from "@/i18n/LanguageContext";
import { deepLocalize } from "@/i18n/translate";
import { PLATFORM_URL } from "@/lib/api";

const releases = [
  { v: "v1.4", date: "2026 Q2", notes: "Expanded regulatory profiles, controls library and executive reporting." },
  { v: "v1.3", date: "2026 Q1", notes: "Continuous monitoring, drift detection and evidence chain enhancements." },
  { v: "v1.2", date: "2025 Q4", notes: "Audit Centre, multi-agent governance and policy management." },
  { v: "v1.0", date: "2025 Q3", notes: "Initial platform: AI inventory, risk register, lifecycle and dashboards." },
];

const faqs = [
  { q: "What is the AI Governance OS?", a: "An enterprise operating system that unifies AI inventory, risk management, lifecycle oversight, audit readiness and evidence in a single verifiable system." },
  { q: "How does it support the EU AI Act?", a: "It helps teams inventory AI systems, classify risk, map obligations to a timeline and maintain the documentation and evidence needed for accountability. It provides tooling support and is not legal advice." },
  { q: "Where can it be deployed?", a: "On-premise, sovereign cloud, private cloud or hybrid — to meet data residency, sovereignty and security requirements." },
  { q: "How is access controlled?", a: "Zero-trust access with provider-agnostic authentication, ready for Keycloak, Microsoft Entra ID, Auth0 or Okta." },
];

const features = [
  { icon: Activity, title: "Executive Dashboard", text: "Real-time posture, maturity, compliance scores and regulatory status for leadership." },
  { icon: Boxes, title: "AI Inventory", text: "Central, versioned AI-BOM of every AI system, model, dataset and dependency." },
  { icon: GitBranch, title: "AI Lifecycle", text: "Governance from design and validation to deployment, monitoring and retirement." },
  { icon: ShieldCheck, title: "Risk Register", text: "Persistent identification, classification and mitigation tracking of AI risks." },
  { icon: Layers, title: "AI Classification", text: "Structured risk-tier classification aligned to regulatory obligations." },
  { icon: Workflow, title: "Governance Workflows", text: "Repeatable approval flows with clear ownership and accountability." },
  { icon: FileCheck2, title: "Evidence Generation", text: "Tamper-evident evidence chains linking controls and decisions to records." },
  { icon: MonitorCheck, title: "Continuous Monitoring", text: "Ongoing oversight of performance, drift and control effectiveness." },
  { icon: BarChart3, title: "Executive Reporting", text: "Governance, compliance and executive reports as portable documents." },
  { icon: ClipboardCheck, title: "Audit Centre", text: "Audit-ready workspace consolidating controls, findings and evidence." },
  { icon: ScrollText, title: "Compliance Management", text: "Track obligations, deferrals and milestones as regulations evolve." },
  { icon: BookOpen, title: "AI Act Compliance", text: "Risk classification, obligation timelines and evidence for AI Act readiness." },
  { icon: Lightbulb, title: "Explainability", text: "Capture explainability and human-oversight records for transparency." },
  { icon: Users, title: "Multi-Agent Governance", text: "Govern autonomous and multi-agent systems with structured oversight." },
  { icon: Library, title: "Policy Management", text: "Author, version and enforce governance policies across the estate." },
  { icon: Gauge, title: "Controls Library", text: "Reusable controls mapped to frameworks and standards." },
  { icon: ClipboardCheck, title: "Assessment Engine", text: "Structured evaluation and scoring across governance dimensions." },
];

const deployment = [
  { icon: Server, title: "On-Premise", text: "Run entirely within your own datacentre for maximum control and data residency." },
  { icon: Cloud, title: "Sovereign & Hybrid Cloud", text: "Jurisdiction-aligned or hybrid environments balancing sovereignty and scale." },
  { icon: Lock, title: "Zero Trust", text: "Every request authenticated and authorised under zero-trust principles." },
  { icon: History, title: "Immutable Audit Trail", text: "Tamper-evident logging of actions, approvals and evidence." },
];

const frameworks = [
  { name: "EU AI Act", text: "Risk classification, obligation timelines and evidence to support AI Act readiness." },
  { name: "ISO/IEC 42001", text: "Structure for an AI management system with controls and continual improvement." },
  { name: "ISO/IEC 27001", text: "Complements information-security governance for AI assets and data." },
  { name: "NIST AI RMF", text: "Supports the Govern, Map, Measure and Manage functions of AI risk." },
];

export default function GovernanceOS() {
  const { lang, tr } = useLang();
  const feats = deepLocalize(features, lang);
  const deploys = deepLocalize(deployment, lang);
  const fws = deepLocalize(frameworks, lang);
  const rels = deepLocalize(releases, lang);
  const qs = deepLocalize(faqs, lang);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "AstroLife AI Governance OS",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, On-Premise, Cloud",
    description: "Enterprise AI Governance Operating System unifying AI inventory, risk, lifecycle, audit readiness and evidence.",
    offers: { "@type": "Offer", availability: "https://schema.org/PreOrder" },
  };

  return (
    <>
      <Seo
        title={tr("AI Governance OS — Enterprise AI Governance Platform")}
        description={tr("AstroLife™ AI Governance OS unifies AI inventory, risk management, lifecycle oversight, audit readiness, EU AI Act compliance and evidence in a single verifiable system.")}
        path="/ai-governance-os"
        keywords="AI Governance OS, AI Governance Platform, AI Governance Software, EU AI Act, AI Inventory, AI Risk Register, AI Audit, Verifiable AI"
        jsonLd={jsonLd}
      />

      <PageHero
        overline={tr("Flagship Platform")}
        title={tr("Enterprise AI Governance Operating System")}
        subtitle={tr("AstroLife™ AI Governance OS helps organisations govern artificial intelligence with confidence — unifying inventory, risk, lifecycle, audit readiness and evidence in a single, verifiable system.")}
        breadcrumbs={[{ label: tr("AI Governance OS") }]}
      >
        <div className="mt-9 flex flex-col gap-4 sm:flex-row">
          <a
            href={PLATFORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="os-access-platform"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-astro-blue px-8 py-4 font-semibold text-white hover:bg-astro-light transition-colors"
          >
            {tr("Access Platform")} <ArrowUpRight className="h-4 w-4" />
          </a>
          <Link
            to="/private-beta"
            data-testid="os-request-demo"
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/20 px-8 py-4 font-semibold text-white hover:bg-white/10 transition-colors"
          >
            {tr("Request Demo")}
          </Link>
        </div>
      </PageHero>

      {/* Intro */}
      <section className="bg-navy py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="grid gap-10 md:grid-cols-2">
              <p className="text-lg md:text-xl leading-relaxed text-silver/80">
                {tr("Effective governance requires more than policy documents. AstroLife AI Governance OS operationalises governance by turning principles into repeatable controls, clear ownership and measurable posture.")}
              </p>
              <p className="text-lg md:text-xl leading-relaxed text-silver/70">
                {tr("Built around auditability and traceability: actions, approvals and evidence form a durable, reviewable record across the full AI lifecycle.")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Capabilities grid */}
      <section className="border-y border-navy-border bg-navy-surface py-24 lg:py-32" aria-labelledby="capabilities">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("Capabilities")} title={tr("One operating system for accountable, organisation-wide AI oversight.")} />
          <Stagger className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-navy-border bg-navy-border sm:grid-cols-2 lg:grid-cols-3">
            {feats.map((f) => (
              <motion.div key={f.title} variants={staggerItem} className="group bg-navy-surface p-7 transition-colors hover:bg-navy">
                <f.icon className="h-6 w-6 text-astro-light" />
                <h3 className="mt-5 font-heading text-base font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-silver/60">{f.text}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Deployment */}
      <section className="bg-navy py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("Deployment & Security")} title={tr("Deploy where your sovereignty and security requirements demand.")} />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {deploys.map((d) => (
              <motion.div key={d.title} variants={staggerItem} className="rounded-2xl border border-navy-border bg-navy-surface p-7">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-astro-blue/10 text-astro-light">
                  <d.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-white">{d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-silver/60">{d.text}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Frameworks */}
      <section className="border-t border-navy-border bg-navy-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            overline={tr("Standards & Frameworks")}
            title={tr("Supporting capabilities aligned with leading frameworks.")}
            sub={tr("The platform provides tooling support and does not constitute legal advice.")}
          />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2">
            {fws.map((f) => (
              <motion.div key={f.name} variants={staggerItem} className="flex gap-5 rounded-2xl border border-navy-border bg-navy p-7">
                <div className="font-heading text-2xl font-black text-astro-light">{f.name}</div>
                <p className="text-sm leading-relaxed text-silver/60 self-center">{f.text}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Release History */}
      <section className="bg-navy py-24 lg:py-28" id="releases">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("Release History")} title={tr("A disciplined, transparent release cadence.")} />
          <div className="mt-12 space-y-3">
            {rels.map((r, i) => (
              <Reveal key={r.v} delay={i * 0.04}>
                <div className="flex flex-col gap-2 rounded-2xl border border-navy-border bg-navy-surface p-6 sm:flex-row sm:items-center sm:gap-8">
                  <div className="flex items-baseline gap-3 sm:w-40 sm:flex-shrink-0">
                    <span className="font-heading text-xl font-black text-white">{r.v}</span>
                    <span className="text-xs uppercase tracking-wider text-astro-light">{r.date}</span>
                  </div>
                  <p className="text-sm text-silver/70">{r.notes}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-navy-border bg-navy-surface py-24 lg:py-28" id="faq">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("FAQ")} title={tr("Frequently asked questions.")} />
          <Reveal>
            <Accordion type="single" collapsible className="mx-auto mt-12 max-w-3xl">
              {qs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-navy-border">
                  <AccordionTrigger className="text-left font-heading text-base font-semibold text-white hover:text-astro-light hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-silver/70 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <PlatformCta />
    </>
  );
}
